import { BEM } from '@feedfm/feed-util';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Table, TableProps } from 'react-bootstrap';
import { CountryList } from '~/components/CountryList/CountryList';
import { SimpleCountryFragment } from '~/graphqlTypes/SimpleCountryFragment';
import { AudioFileAvailabilityFragment } from '~/graphqlTypes/AudioFileAvailabilityFragment';
import { objectsAreEqualByValue } from '~/util/objects';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faX, faEdit, faGlobe } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { CountryInclusionMode } from '~/graphqlTypes/globalTypes';
import { CountriesDropdown } from '~/components/CountriesDropdown/CountriesDropdown';
import { StreamingRightsFragment } from '~/graphqlTypes/StreamingRightsFragment';
import { AudioFileAvailabilityModal } from '~/components/AudioFileAvailabilityModal/AudioFileAvailabilityModal';

import { AudioFileAvailabilityPreviewFragment } from '~/graphqlTypes/AudioFileAvailabilityPreviewFragment';
import { useAudioFileAvailabilityModal } from '~/components/AudioFileAvailabilityModal/useAudioFileAvailabilityModal';
import { AppContext } from '~/AppContext';

import './AudioFileAvailabilityTable.scss';

type EitherFragment = AudioFileAvailabilityFragment | AudioFileAvailabilityPreviewFragment;

export interface AudioFileAvailabilityReadOnlyTableProps extends TableProps {
  /** The rights to display. */
  rights: Array<EitherFragment>;
  readonly: true;
  handleUpdateRights?: undefined;
  handleDeleteRights?: undefined;
}

export interface AudioFileAvailabilityReadWriteTableProps extends TableProps {
  /** The rights to display/edit. If null, nothing will appear. */
  rights: Array<AudioFileAvailabilityFragment>;
  readonly?: undefined | false;
  handleUpdateRights: (
    updated: AudioFileAvailabilityFragment,
    original?: AudioFileAvailabilityFragment,
  ) => void | Promise<void>;
  handleDeleteRights: ( right: AudioFileAvailabilityFragment ) => void | Promise<void>;
}

export type AudioFileAvailabilityTableProps =
  | AudioFileAvailabilityReadOnlyTableProps
  | AudioFileAvailabilityReadWriteTableProps;

/**
 * This is used to generate a new (empty) availability fragment pre-populated with a specific right.
 */
const newAvailabilityModel = ( right: StreamingRightsFragment ): AudioFileAvailabilityFragment => ( {
  __typename: 'AudioFileAvailability',
  id: -1,
  streamingRights: right,
  countryAvailability: {
    __typename: 'CountryAvailability',
    mode: CountryInclusionMode.include_all,
    countriesAndDates: [],
  },
} );

export const AudioFileAvailabilityTable: React.FC<AudioFileAvailabilityTableProps> = ( props ) => {
  const { readonly, rights, handleDeleteRights, handleUpdateRights, ...tableProps } = props;
  const bem = new BEM( 'ops-audio-file-availability-table', [] );

  const { streamingRights: allStreamingRights } = useContext( AppContext );
  const [countryFilter, setCountryFilter] = useState<SimpleCountryFragment | null>( null );
  const [filteredRights, setFilteredList] = useState<Array<EitherFragment>>( rights );
  const [newRightValue, setNewRightValue] = useState<StreamingRightsFragment | null>( null );

  const handleColumnFilter = useCallback( ( country: SimpleCountryFragment | null ) => {
    setCountryFilter( country );
  }, [] );

  useEffect( () => {
    if ( countryFilter ) {
      const newList = rights?.filter(
        // Filters down to any `right` that includes the filtered country or is include_all
        ( { countryAvailability: { countriesAndDates, mode } } ) =>
          mode === CountryInclusionMode.include_all ||
          countriesAndDates?.some(
            ( { country: { code } } ) =>
              ( mode === CountryInclusionMode.include && code.includes( countryFilter.code ) ) ||
              ( mode === CountryInclusionMode.exclude && !code.includes( countryFilter.code ) ),
          ),
      );

      setFilteredList( newList ?? null );
    } else {
      setFilteredList( rights );
    }
  }, [countryFilter, rights] );

  const { showing, show, controls } = useAudioFileAvailabilityModal();

  const editModel = useCallback(
    ( right: AudioFileAvailabilityFragment, isNew: boolean ) => {
      if ( handleUpdateRights === undefined ) {
        throw new Error( 'Cannot edit rights in read-only mode.' );
      }
      setNewRightValue( null );
      show( {
        initialModel: right,
        saveHandler: ( newRight ) => handleUpdateRights( newRight, isNew ? undefined : right ),
        isNew,
      } );
    },
    [handleUpdateRights, show],
  );

  const handleDelete = useCallback(
    ( right: AudioFileAvailabilityFragment ) => () => {
      if ( handleDeleteRights === undefined ) {
        throw new Error( 'Cannot delete rights in read-only mode.' );
      }

      if ( window.confirm( 'Are you sure you want to delete this streaming right? This cannot be undone.' ) ) {
        handleDeleteRights( right );
      }
    },
    [handleDeleteRights],
  );

  const [showCountryFilter, setShowCountryFilter] = useState( false );
  const toggleShowCountryFilter = () => setShowCountryFilter( !showCountryFilter );

  const unassignedRights = allStreamingRights.filter(
    ( right ) => rights === null || !rights.some( ( { streamingRights } ) => objectsAreEqualByValue( streamingRights, right ) ),
  );

  const targetHeight = 30;

  // TLDR: make ReactSelect slightly smaller.
  const styles = {
    control: ( base: any ) => ( {
      ...base,
      minHeight: 'initial',
    } ),
    valueContainer: ( base: any ) => ( {
      ...base,
      height: `${targetHeight - 1 - 1}px`,
      padding: '0 8px',
    } ),
    clearIndicator: ( base: any ) => ( {
      ...base,
      padding: `${( targetHeight - 20 - 1 - 1 ) / 2}px`,
    } ),
    dropdownIndicator: ( base: any ) => ( {
      ...base,
      padding: `${( targetHeight - 20 - 1 - 1 ) / 2}px`,
    } ),
  };

  return (
    <section className={ BEM.$( bem ) } data-testid="ops-audio-file-availability-table">
      <Table size="sm" striped { ...tableProps }>
        <thead>
          <tr>
            <th>Right</th>
            <th>
              Countries
              <FontAwesomeIcon
                className={ BEM.$( bem.el( 'countries-icon', { active: countryFilter !== null } ) ) }
                icon={ faGlobe }
                onClick={ toggleShowCountryFilter }
              />
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          { showCountryFilter && (
            <tr>
              <td>Only show rights for...</td>
              <td colSpan={ 2 }>
                <CountriesDropdown value={ countryFilter } isClearable onChange={ handleColumnFilter } />
              </td>
            </tr>
          ) }

          { rights.length === 0 && (
            <tr key="empty">
              <td colSpan={ 3 }>No rights defined.</td>
            </tr>
          ) }

          { rights.length > 0 && filteredRights.length === 0 && (
            <tr key="empty">
              <td colSpan={ 3 }>No rights visible.</td>
            </tr>
          ) }

          { filteredRights.length > 0 &&
            filteredRights.map( ( right, index ) => (
              <tr key={ index }>
                <td className={ BEM.$( bem.el( 'cell' ) ) }>
                  <code>{ right.streamingRights.name }</code>
                </td>
                <td className={ BEM.$( bem.el( 'cell' ) ) }>
                  <CountryList
                    mode={ right.countryAvailability.mode }
                    countries={ right.countryAvailability.countriesAndDates }
                  />
                </td>
                { readonly !== true && (
                  <td className={ BEM.$( bem.el( 'row-actions' ) ) }>
                    <Button
                      // The props union guarantees that when readonly !== true, this is an
                      // AudioFileAvailabilityFragment.
                      onClick={ () => editModel( right as AudioFileAvailabilityFragment, false ) }
                      size="sm"
                      className={ BEM.$( bem.el( 'action' ) ) }
                    >
                      <FontAwesomeIcon icon={ faEdit } />
                    </Button>{ ' ' }
                    <Button
                      // The props union guarantees that when readonly !== true, this is an
                      // AudioFileAvailabilityFragment.
                      onClick={ handleDelete( right as AudioFileAvailabilityFragment ) }
                      variant="danger"
                      size="sm"
                      className={ BEM.$( bem.el( 'action' ) ) }
                    >
                      <FontAwesomeIcon icon={ faX } />
                    </Button>
                  </td>
                ) }
              </tr>
            ) ) }
        </tbody>
      </Table>
      { readonly !== true && unassignedRights.length === 0 && <>All rights have been assigned.</> }

      { readonly !== true && unassignedRights.length > 0 && (
        <Select
          styles={ styles }
          maxMenuHeight={ 150 }
          isClearable
          onChange={ ( right ) => right && editModel( newAvailabilityModel( right ), true ) }
          value={ newRightValue }
          options={ unassignedRights }
          formatOptionLabel={ ( { name, description } ) => (
            <>
              { name } &mdash; { description }
            </>
          ) }
          getOptionValue={ ( right ) => right.name }
          placeholder="Configure new right..."
        />
      ) }

      { readonly !== true && showing && (
        <AudioFileAvailabilityModal
          modalControls={ controls }
          allStreamingRights={ allStreamingRights }
          readonlyRight={ true }
          title={ controls.isNew ? 'Add Streaming Right' : 'Edit Streaming Right' }
        />
      ) }
    </section>
  );
};
